import { IScope, IPromise } from "angular";
import { IModalService } from "angular-ui-bootstrap";

import { ICollection } from "@gtmhub/models";
import { IBadge } from "../models/badges";
import { IAssigneeIdMap } from "@gtmhub/assignees/models";
import { BadgeService } from "../services/badges";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { IStateInit } from "@gtmhub/core/routing";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { INgRedux } from "@gtmhub/state-management";
import { BadgesModalsService } from "../services/badgesModals";

interface IBadgesScope extends IScope {
  indicators: { loading: boolean; delete: boolean; gettingBadges: boolean };
  badges: ICollection<IBadge>;
  assigneesIdMap: IAssigneeIdMap;
  openDeleteDialog(badgeId: string);
  openAwardedDialog(badgeId: string);
  openAwardDialog(badgeId: string);
}

const bindStateToScope = (state: IAssigneesStoreState) => ({
  assigneesIdMap: state.assignees.map,
});

export class BadgesCtrl implements IStateInit {
  public static $inject = ["$scope", "$uibModal", "BadgeService", "BadgesModalsService", "UIErrorHandlingService", "AssigneeActions", "$ngRedux"];

  constructor(
    private $scope: IBadgesScope,
    private $uibModal: IModalService,
    private badgeService: BadgeService,
    private badgesModalsService: BadgesModalsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private $ngRedux: INgRedux
  ) {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());

    this.$scope.openDeleteDialog = this.openDeleteDialog;
    this.$scope.openAwardedDialog = this.openAwardedDialog;
    this.$scope.openAwardDialog = this.openAwardDialog;
    this.$scope.$on("accountBadgesUpdated", this.getBadges);
    this.$scope.indicators = { loading: false, delete: false, gettingBadges: false };
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);

    return this.getBadges();
  };

  private openDeleteDialog = (badgeId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/delete.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        "$uibModalInstance",
        function ($scope, $uibModalInstance) {
          $scope.assigneesIdMap = ctrl.$scope.assigneesIdMap;
          $scope.indicators = { loading: true };
          ctrl.badgeService.getBadgeAssignments([], [], badgeId).then(
            (badgeAssignments) => {
              $scope.badgeAssignments = badgeAssignments;
              $scope.indicators.loading = false;
            },
            (err) => ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "loading" })
          );

          $scope.delete = () => {
            $scope.indicators.delete = true;
            ctrl.badgeService.deleteBadge(badgeId).then(
              () => {
                $scope.indicators.delete = false;
                $uibModalInstance.close();
                ctrl.getBadges();
              },
              (err) => ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "delete" })
            );
          };
        },
      ],
    });
  };

  private openAwardedDialog = (badgeId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const ctrl = this;
    this.$uibModal.open({
      templateUrl: require("../views/awarded.jade"),
      windowClass: "medium-small",
      controller: [
        "$scope",
        function ($scope) {
          $scope.assigneesIdMap = ctrl.$scope.assigneesIdMap;

          $scope.indicators = { loading: true };
          ctrl.badgeService.getBadgeAssignments([], [], badgeId).then(
            (badgeAssignments) => {
              $scope.badgeAssignments = badgeAssignments;
              $scope.indicators.loading = false;
            },
            (err) => ctrl.uiErrorHandlingService.handleIndicator(err, { indicator: "loading" })
          );
        },
      ],
    });
  };

  private openAwardDialog = (badgeId: string): void => {
    this.badgesModalsService.openAwardDialog({ badgeId });
  };

  private getBadges = (): IPromise<unknown> => {
    this.$scope.indicators.gettingBadges = true;

    return this.badgeService.getBadges().then(
      (badges) => {
        this.$scope.indicators.gettingBadges = false;

        const emptyBadgeCollection: ICollection<IBadge> = {
          items: [],
          totalCount: 0,
        };

        this.$scope.badges = badges ? badges : emptyBadgeCollection;

        this.$scope.badges.items = this.$scope.badges.items ? this.$scope.badges.items : [];

        this.$scope.badges.items.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
      },
      (err) => this.uiErrorHandlingService.handleIndicator(err, { indicator: "gettingBadges" })
    );
  };
}
